import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import awsConfig from '../aws-config';

const api = axios.create({
  baseURL: awsConfig.apiEndpoint,
  timeout: 10000,
  headers: { 'Content-Type': 'application/json' }
});

// Attach JWT when a Cognito session exists; no-op when anonymous.
// Uses the ID token (not access token) so Cognito User Pool Authorizer
// receives the email claim alongside the sub.
api.interceptors.request.use(async (config) => {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch {
    // No session — anonymous request
  }
  return config;
});

// Set by MaintenanceProvider on mount to receive push notification of a 503/MAINTENANCE_MODE
// response from any in-flight request, without every call site having to check for it itself.
let onMaintenanceDetected = null;

/**
 * Registers the callback the response interceptor calls when it observes a
 * 503/MAINTENANCE_MODE response. Called once by MaintenanceProvider on mount.
 * @param {(message: string) => void} fn
 */
export function setMaintenanceHandler(fn) {
  onMaintenanceDetected = fn;
}

/**
 * Fetch current killswitch/maintenance status. Unauthenticated — must stay reachable
 * even while maintenance mode is blocking every other route.
 * @returns {Promise<{maintenanceMode: boolean, message: string}>}
 */
export async function getSystemStatus() {
  const { data } = await api.get('/api/system/status');
  return data.data;
}

// Retry once on network errors and timeouts (no response received) -- GET requests only.
// 4xx/5xx responses are not retried — they carry meaningful error payloads.
// Non-idempotent methods (POST/PUT/DELETE) must never be auto-retried here: on a
// client-side timeout the request has no `.response`, but the Lambda invocation it
// triggered keeps running server-side regardless (API Gateway doesn't cancel it just
// because the client gave up waiting) -- retrying a "create" POST after a timeout can
// silently double-create the resource if the original request eventually succeeds too
// (this is exactly what caused duplicate Draft rows from a single slow ESPN/Yahoo
// import-draft call). Callers that need timeout resilience for a POST should build
// their own explicit retry with a request-level idempotency safeguard instead.
api.interceptors.response.use(
  response => response,
  async (error) => {
    const config = error.config;
    // A 503/MAINTENANCE_MODE response always carries `.response` (it's a real server reply,
    // not a network failure), so this branch and the `!error.response` GET-retry branch below
    // are mutually exclusive for the same error -- never both fire.
    if (error.response?.status === 503 && error.response?.data?.error?.code === 'MAINTENANCE_MODE') {
      onMaintenanceDetected?.(error.response.data.error.message);
      return Promise.reject(error);
    }
    if (config && config.method === 'get' && !config._retried && !error.response) {
      config._retried = true;
      await new Promise(resolve => setTimeout(resolve, 1000));
      return api(config);
    }
    return Promise.reject(error);
  }
);

/**
 * Fetch rankings, optionally filtered by source and scoring format.
 * @param {string} [source] - Source name to filter by
 * @param {string} [scoringFormat] - 'standard' | 'ppr'
 * @returns {Promise<Array>} Rankings array
 */
export async function getRankings(source, scoringFormat = 'ppr') {
  const params = {};
  if (source) params.source = source;
  if (scoringFormat) params.scoringFormat = scoringFormat;
  const { data } = await api.get('/api/rankings', { params });
  return data.data || [];
}

/**
 * Upload CSV rankings for a named source.
 * @param {string} sourceName
 * @param {string} csvContent
 * @param {string} [scoringFormat] - 'standard' | 'ppr'
 * @returns {Promise<Object>} Upload result
 */
export async function uploadRankings(sourceName, csvContent, scoringFormat = 'standard') {
  const { data } = await api.post('/api/rankings', { sourceName, csvContent, scoringFormat });
  return data.data;
}

/**
 * Get aggregated consensus rankings for a given scoring format.
 * @param {string} [scoringFormat] - 'standard' | 'ppr'
 * @returns {Promise<Array>} Aggregated rankings array
 */
export async function getAggregatedRankings(scoringFormat = 'ppr') {
  const { data } = await api.post('/api/aggregate', null, {
    params: { scoringFormat },
  });
  return data.data || [];
}

/**
 * Get personal aggregate consensus rankings (includes the caller's uploaded sources).
 * Requires authentication.
 * @param {string} [scoringFormat] - 'standard' | 'ppr'
 * @returns {Promise<Array>} Aggregated rankings array
 */
export async function getPersonalAggregatedRankings(scoringFormat = 'ppr') {
  const { data } = await api.post('/api/aggregate/personal', null, {
    params: { scoringFormat },
  });
  return data.data || [];
}

/**
 * Get the list of ranking sources the caller has uploaded.
 * Requires authentication.
 * @returns {Promise<Array>} SourceDTO list
 */
export async function getUserSources() {
  const { data } = await api.get('/api/user-rankings/sources');
  return data.data || [];
}

/**
 * Get the caller's uploaded rankings for a specific source.
 * Requires authentication.
 * @param {string} sourceName
 * @param {string} [scoringFormat] - 'standard' | 'ppr'
 * @returns {Promise<Array>} RankingDTO list
 */
export async function getUserRankings(sourceName, scoringFormat = 'standard') {
  const { data } = await api.get('/api/user-rankings', {
    params: { sourceName, scoringFormat },
  });
  return data.data || [];
}

/**
 * Delete all of the caller's uploaded rankings for a specific source.
 * Requires authentication.
 * @param {string} sourceName
 * @returns {Promise<Object>} { deleted: N }
 */
export async function deleteUserSource(sourceName) {
  const { data } = await api.delete('/api/user-rankings', {
    params: { sourceName },
  });
  return data.data;
}

/**
 * Get list of available ranking sources.
 * @returns {Promise<Array>} Sources array
 */
export async function getSources() {
  const { data } = await api.get('/api/sources');
  return data.data || [];
}

/**
 * Get the current user's profile.
 * @returns {Promise<Object>} Profile object
 */
export async function getProfile() {
  const { data } = await api.get('/api/profile');
  return data.data;
}

/**
 * Update display name on the current user's profile.
 * @param {string} displayName
 * @returns {Promise<Object>} Updated profile
 */
export async function updateProfile(displayName) {
  const { data } = await api.put('/api/profile', { displayName });
  return data.data;
}

/**
 * List the current user's saved drafts.
 * @returns {Promise<Array>} Draft summaries
 */
export async function getUserDrafts() {
  const { data } = await api.get('/api/profile/drafts');
  return data.data || [];
}

// ─── Subscription Endpoints ────────────────────────────────────────────────────

/**
 * Create a Stripe Checkout session for upgrading to Pro.
 * @returns {Promise<Object>} { url }
 */
export async function createCheckoutSession() {
  const { data } = await api.post('/api/subscription/create-checkout');
  return data.data;
}

/**
 * Get a Stripe Billing Portal session URL for the current Pro subscriber.
 * @returns {Promise<Object>} { url }
 */
export async function getBillingPortalUrl() {
  const { data } = await api.get('/api/subscription/portal');
  return data.data;
}

/**
 * List the current user's leagues.
 * @returns {Promise<Array>} League summaries
 */
export async function listLeagues() {
  const { data } = await api.get('/api/league');
  return data.data || [];
}

/**
 * Create a new league.
 * @param {Object} request - CreateLeagueRequest (includes seasonType: 'REDRAFT'|'DYNASTY')
 * @returns {Promise<Object>} LeagueDTO (includes seasonType)
 */
export async function createLeague(request) {
  const { data } = await api.post('/api/league', request);
  return data.data;
}

/**
 * Get a league with full roster.
 * @param {string} leagueId
 * @returns {Promise<Object>} LeagueDTO
 */
export async function getLeague(leagueId) {
  const { data } = await api.get(`/api/league/${leagueId}`);
  return data.data;
}

/**
 * Delete a league and its roster.
 * @param {string} leagueId
 * @returns {Promise<void>}
 */
export async function deleteLeague(leagueId) {
  await api.delete(`/api/league/${leagueId}`);
}

/**
 * Create a league pre-populated from a completed draft.
 * @param {string} draftId
 * @param {Object} request - CreateLeagueRequest
 * @returns {Promise<Object>} LeagueDTO
 */
export async function createLeagueFromDraft(draftId, request) {
  const { data } = await api.post(`/api/league/from-draft/${draftId}`, request);
  return data.data;
}

/**
 * Returns the league created from this draft, or throws a 404 error if none exists.
 * @param {string} draftId
 * @returns {Promise<Object>} LeagueDTO summary
 */
export async function getLeagueBySourceDraft(draftId) {
  const { data } = await api.get(`/api/league/from-draft/${draftId}`);
  return data.data;
}

/**
 * Replace all roster rows for a league.
 * @param {string} leagueId
 * @param {Array} roster - List of LeagueRosterDTO
 * @returns {Promise<Object>} Updated LeagueDTO
 */
export async function updateLeagueRoster(leagueId, roster) {
  const { data } = await api.put(`/api/league/${leagueId}/roster`, roster);
  return data.data;
}

/**
 * Get a Yahoo OAuth authorization URL.
 * @returns {Promise<Object>} { url, state }
 */
export async function getYahooAuthUrl() {
  const { data } = await api.get('/api/yahoo/auth-url');
  return data.data;
}

/**
 * Exchange a Yahoo OAuth code for tokens.
 * @param {string} code
 * @returns {Promise<void>}
 */
export async function handleYahooCallback(code) {
  await api.post('/api/yahoo/callback', null, { params: { code } });
}

/**
 * Check whether this user already has a stored Yahoo token, so the UI can offer to reuse it
 * instead of re-prompting for OAuth.
 * @returns {Promise<Object>} { exists }
 */
export async function getYahooTokenStatus() {
  const { data } = await api.get('/api/yahoo/token-status');
  return data.data;
}

/**
 * List all of the user's Yahoo NFL fantasy leagues across all seasons.
 * @returns {Promise<Array>} YahooLeagueDTO list
 */
export async function getYahooLeagues() {
  const { data } = await api.get('/api/yahoo/leagues');
  return data.data || [];
}

/**
 * Preview a roster import from a Yahoo league (does not persist).
 * @param {string} yahooLeagueKey
 * @returns {Promise<Array>} LeagueRosterDTO list
 */
export async function importYahooRoster(yahooLeagueKey) {
  const { data } = await api.post('/api/yahoo/import', null, { params: { yahooLeagueKey } });
  return data.data || [];
}

/**
 * Fetch scoring rules from a Yahoo league, mapped to internal stat keys.
 * @param {string} yahooLeagueKey
 * @returns {Promise<Object>} Map of stat key → point value
 */
export async function getYahooScoringSettings(yahooLeagueKey) {
  const { data } = await api.get('/api/yahoo/scoring-settings', { params: { yahooLeagueKey } });
  return data.data || {};
}

/**
 * Fetch starting-lineup slot counts from a Yahoo league, for League.rosterConfig at creation time.
 * @param {string} yahooLeagueKey
 * @returns {Promise<Object>} Map of position key → slot count
 */
export async function getYahooRosterPositions(yahooLeagueKey) {
  const { data } = await api.get('/api/yahoo/roster-positions', { params: { yahooLeagueKey } });
  return data.data || {};
}

/**
 * Returns all teams in a Yahoo league, each with teamName, yahooTeamKey, and isUserTeam flag.
 * @param {string} yahooLeagueKey
 * @returns {Promise<Array>} YahooTeamDTO list
 */
export async function getYahooLeagueTeams(yahooLeagueKey) {
  const { data } = await api.get(`/api/yahoo/leagues/${encodeURIComponent(yahooLeagueKey)}/teams`);
  return data.data || [];
}

/**
 * Imports a completed Yahoo draft's results into a new FantasyAssist draft.
 * @param {string} yahooLeagueKey
 * @param {Object} [opponentLinksByTeamKey] - Map of Yahoo team_key -> opponentId to link on the imported draft
 * @param {string} [leagueId] - If a league was already created for this import (create-league-then-import-draft
 *   flow), links the new draft back to it (League.sourceDraftId) so opponent-linking and other league<->draft
 *   correlation works the same way it does for the native draft-to-league conversion flow.
 * @returns {Promise<Object>} DraftDTO
 */
export async function importYahooDraft(yahooLeagueKey, opponentLinksByTeamKey, leagueId) {
  const body = { ...(opponentLinksByTeamKey && { opponentLinksByTeamKey }), ...(leagueId && { leagueId }) };
  const res = await api.post(
    `/api/yahoo/leagues/${encodeURIComponent(yahooLeagueKey)}/import-draft`,
    Object.keys(body).length > 0 ? body : undefined,
    // Several sequential Yahoo API calls happen server-side (settings, teams, draft
    // results, player batches) -- the default 10s client timeout is too tight for a cold
    // Lambda invocation. 28s stays under API Gateway's 29s hard REST integration ceiling.
    { timeout: 28000 }
  );
  return res.data.data;
}

/**
 * Saves the user's ESPN SWID/espn_s2 cookies.
 * @param {string} swid
 * @param {string} espnS2
 */
export async function saveEspnCredentials(swid, espnS2) {
  await api.post('/api/espn/credentials', { swid, espnS2 });
}

/**
 * Checks whether the current user already has a stored ESPN credentials row, so the frontend
 * can skip re-showing the "Connect ESPN Account" modal on repeat league creations.
 * @returns {Promise<Object>} { exists: boolean }
 */
export async function getEspnCredentialStatus() {
  const { data } = await api.get('/api/espn/credentials/status');
  return data.data;
}

/**
 * Fetches league settings and a team list preview for the given ESPN league/season.
 * @param {string} espnLeagueId
 * @param {number} seasonYear
 * @returns {Promise<Object>} { league: EspnLeagueDTO, teams: EspnTeamDTO[] }
 */
export async function getEspnLeague(espnLeagueId, seasonYear) {
  const { data } = await api.get(`/api/espn/leagues/${encodeURIComponent(espnLeagueId)}`, {
    params: { seasonYear },
  });
  return data.data;
}

/**
 * Imports a completed ESPN draft's results into a new FantasyAssist draft.
 * @param {string} espnLeagueId
 * @param {number} seasonYear
 * @param {Object} [opponentLinksByTeamKey] - Map of ESPN team id -> opponentId to link on the imported draft
 * @param {string} [leagueId] - If a league was already created for this import (create-league-then-import-draft
 *   flow), links the new draft back to it (League.sourceDraftId) so opponent-linking and other league<->draft
 *   correlation works the same way it does for the native draft-to-league conversion flow.
 * @returns {Promise<Object>} DraftDTO
 */
export async function importEspnDraft(espnLeagueId, seasonYear, opponentLinksByTeamKey, leagueId) {
  const body = { ...(opponentLinksByTeamKey && { opponentLinksByTeamKey }), ...(leagueId && { leagueId }) };
  const res = await api.post(
    `/api/espn/leagues/${encodeURIComponent(espnLeagueId)}/import-draft`,
    Object.keys(body).length > 0 ? body : undefined,
    // Several sequential ESPN API calls happen server-side (settings, teams, draft
    // details with up-to-3-attempt 202 retries, and a full active-player-catalog fetch
    // of 1500+ players) -- the default 10s client timeout is too tight for a cold Lambda
    // invocation. 28s stays under API Gateway's 29s hard REST integration ceiling.
    { params: { seasonYear }, timeout: 28000 }
  );
  return res.data.data;
}

/**
 * Replace all scoring rules for a league.
 * @param {string} leagueId
 * @param {Object} scoringRules - Map of stat key → point value
 * @returns {Promise<Object>} Updated LeagueDTO
 */
export async function updateLeagueScoringRules(leagueId, scoringRules) {
  const { data } = await api.put(`/api/league/${leagueId}/scoring`, scoringRules);
  return data.data;
}

/**
 * Replace the starting-lineup slot requirements (rosterConfig) for a league.
 * @param {string} leagueId
 * @param {Object} rosterConfig - Map of position key → starting-slot count
 * @returns {Promise<Object>} Updated LeagueDTO
 */
export async function updateLeagueRosterConfig(leagueId, rosterConfig) {
  const { data } = await api.put(`/api/league/${leagueId}/roster-config`, rosterConfig);
  return data.data;
}

/**
 * Create a new draft session. Requires authentication.
 * @param {Object} request - {teamCount, rounds, draftType, userTeamPosition, teamNames, draftName,
 *   sport, seasonType ('REDRAFT'|'DYNASTY'), excludedPlayers (string[]; dynasty pre-draft exclusions),
 *   commissionerMode (boolean), pickTimerSeconds (number|null)}
 * @returns {Promise<Object>} Draft DTO (includes seasonType, excludedPlayers)
 */
export async function createDraft(request) {
  const { data } = await api.post('/api/draft/create', request);
  return data.data;
}

// Commissioner Mode: a guest has no Cognito account, so their pick-authorization credential is a
// per-draft bearer token minted at join time (see JoinDraftResponse.guestToken) instead of a JWT.
// Stored per-draftId so a browser can be a guest in more than one draft at once.
//
// sessionStorage, not localStorage: localStorage is shared by every tab/window in the same storage
// partition, including multiple windows opened within one private-browsing session -- two guests
// joining the same draft from windows of that same session would silently clobber each other's
// token/slotIndex under this shared key, converging every open tab on whichever guest joined most
// recently (both the "(You)" label and pick authorization would point at the wrong slot).
// sessionStorage is scoped per top-level browsing context even within that same session, while
// still surviving a reload within that one tab, which is all reconnect-safety actually requires.
const GUEST_TOKEN_KEY_PREFIX = 'fa_draft_guest_token_';

/** @param {string} draftId @param {string} token */
export function storeDraftGuestToken(draftId, token) {
  sessionStorage.setItem(GUEST_TOKEN_KEY_PREFIX + draftId, token);
}

/** @param {string} draftId @returns {string|null} */
export function getDraftGuestToken(draftId) {
  return sessionStorage.getItem(GUEST_TOKEN_KEY_PREFIX + draftId);
}

// A guest's own slotIndex is returned once at join time, but a general poll response never
// exposes which slot a given guest token belongs to, by design -- see the security note on
// inviteToken. Stored alongside the token so the UI can answer "is it my turn" for a guest the
// same way it already can for a member (via memberUserId). This cache goes stale the moment the
// commissioner reorders slots (DraftService.swapDraftSlots moves a guest's identity to a
// different draft_teams row without ever touching slot_index) -- see getGuestSlotIndex below,
// which DraftContext's poll loop calls to keep this self-healing rather than trusting it forever.
const GUEST_SLOT_KEY_PREFIX = 'fa_draft_guest_slot_';

/** @param {string} draftId @param {number} slotIndex */
export function storeDraftGuestSlotIndex(draftId, slotIndex) {
  sessionStorage.setItem(GUEST_SLOT_KEY_PREFIX + draftId, String(slotIndex));
}

/** @param {string} draftId @returns {number|null} */
export function getDraftGuestSlotIndex(draftId) {
  const raw = sessionStorage.getItem(GUEST_SLOT_KEY_PREFIX + draftId);
  return raw === null ? null : Number(raw);
}

/**
 * Re-resolves a guest's own current slot index from the server, authenticated by their stored
 * guest token -- the only way to detect a commissioner's reorder (see the module comment above).
 * Returns null (never throws) if there's no stored token or the caller isn't a recognized guest
 * for this draft, so callers can treat both cases identically.
 * @param {string} draftId
 * @returns {Promise<number|null>}
 */
export async function getGuestSlotIndex(draftId) {
  const token = getDraftGuestToken(draftId);
  if (!token) return null;
  try {
    const { data } = await api.get(`/api/draft/guest/${draftId}/slot`, guestTokenRequestConfig(draftId));
    return data.data.slotIndex;
  } catch {
    return null;
  }
}

function guestTokenRequestConfig(draftId) {
  const token = getDraftGuestToken(draftId);
  return token ? { headers: { 'X-Draft-Guest-Token': token } } : {};
}

async function hasActiveSession() {
  try {
    const session = await fetchAuthSession();
    return !!session?.tokens?.idToken;
  } catch {
    return false;
  }
}

/**
 * Join a Commissioner Mode draft room by its 5-character code. An already-authenticated caller
 * joins as a member (via the Cognito-gated /join/member route, displayName ignored); everyone else
 * joins as a guest (via the public, no-auth /join route). These are deliberately separate backend
 * routes, not one route branching on a header -- API Gateway never runs the Cognito authorizer on a
 * route with no attached Auth block, so a single "public" route could never actually recognize a
 * logged-in caller's JWT. On a guest join, the response's guestToken is stored automatically for
 * subsequent requests.
 * @param {string} joinCode
 * @param {string} [displayName]
 * @param {string} [teamName] Applied directly to the slot this join claims; blank/omitted keeps "Team N".
 * @returns {Promise<Object>} {draft, slotIndex, guestToken}
 */
export async function joinDraft(joinCode, displayName, teamName) {
  const path = (await hasActiveSession()) ? '/api/draft/join/member' : '/api/draft/join';
  const { data } = await api.post(path, { joinCode, displayName, teamName });
  const result = data.data;
  if (result.guestToken) {
    storeDraftGuestToken(result.draft.draftId, result.guestToken);
    storeDraftGuestSlotIndex(result.draft.draftId, result.slotIndex);
  }
  return result;
}

/**
 * Starts a Commissioner Mode draft, transitioning it out of its lobby. Commissioner only.
 * @param {string} draftId
 * @returns {Promise<Object>} Updated draft DTO
 */
export async function startDraft(draftId) {
  const { data } = await api.post(`/api/draft/${draftId}/start`);
  return data.data;
}

/**
 * Swap two slots' occupants (member/guest/AI assignment, team name, owner flag) in a Commissioner
 * Mode draft's lobby. Commissioner only; draft must still be in LOBBY status.
 * @param {string} draftId
 * @param {number} slotIndexA
 * @param {number} slotIndexB
 * @returns {Promise<Object>} Updated draft DTO
 */
export async function swapDraftSlots(draftId, slotIndexA, slotIndexB) {
  const { data } = await api.put(`/api/draft/${draftId}/slots/reorder`, { slotIndexA, slotIndexB });
  return data.data;
}

/**
 * Renames a single slot's team name in a Commissioner Mode draft's lobby. Commissioner only;
 * draft must still be in LOBBY status.
 * @param {string} draftId
 * @param {number} slotIndex
 * @param {string} teamName
 * @returns {Promise<Object>} Updated draft DTO
 */
export async function updateTeamName(draftId, slotIndex, teamName) {
  const { data } = await api.put(`/api/draft/${draftId}/teams/${slotIndex}/name`, { teamName });
  return data.data;
}

/**
 * Skip the current (expired) pick with no player selected. Commissioner only.
 * @param {string} draftId
 * @returns {Promise<Object>} Updated draft DTO
 */
export async function skipExpiredPick(draftId) {
  const { data } = await api.post(`/api/draft/${draftId}/timer/skip`);
  return data.data;
}

/**
 * Extends the current pick's clock by the given number of seconds. Commissioner only.
 * @param {string} draftId
 * @param {number} additionalSeconds
 * @returns {Promise<Object>} Updated draft DTO
 */
export async function extendTimer(draftId, additionalSeconds) {
  const { data } = await api.post(`/api/draft/${draftId}/timer/extend`, { additionalSeconds });
  return data.data;
}

/**
 * "Save to My Drafts" for a guest who has since logged in, on a completed draft: links their
 * guest-authenticated slot (proven via the stored guest token) to their now-logged-in account, and
 * adds the draft to their profile. Cognito-gated (the axios interceptor attaches the caller's JWT)
 * but also sends the stored guest token, since that's the only proof of which slot is actually
 * theirs -- see guestTokenRequestConfig.
 * @param {string} draftId
 * @returns {Promise<Object>} Updated draft DTO
 */
export async function saveGuestDraftToAccount(draftId) {
  const { data } = await api.post(`/api/draft/${draftId}/save-to-account`, null, guestTokenRequestConfig(draftId));
  return data.data;
}

/** @param {string} draftId */
export function clearDraftGuestToken(draftId) {
  sessionStorage.removeItem(GUEST_TOKEN_KEY_PREFIX + draftId);
  sessionStorage.removeItem(GUEST_SLOT_KEY_PREFIX + draftId);
}

/**
 * Fetch a draft by ID. A stored guest token (see storeDraftGuestToken) routes to the public,
 * no-auth /guest/{id}/room endpoint; otherwise the Cognito-gated /api/draft/{id} route is used
 * (relies on the axios interceptor to attach the caller's JWT) -- see joinDraft's comment for why
 * these can't be a single route.
 * @param {string} draftId
 * @returns {Promise<Object>} Draft DTO with picks
 */
export async function getDraftById(draftId) {
  const guestToken = getDraftGuestToken(draftId);
  const path = guestToken ? `/api/draft/guest/${draftId}/room` : `/api/draft/${draftId}`;
  const { data } = await api.get(path, guestTokenRequestConfig(draftId));
  return data.data;
}

/**
 * Record a pick in the current draft slot. A stored guest token routes to the public
 * /guest/{id}/pick endpoint; otherwise the Cognito-gated /pick route is used (owner, or for
 * Commissioner Mode, a logged-in member whose turn it is).
 * @param {string} draftId
 * @param {Object} pick - {playerName, position, team}
 * @returns {Promise<Object>} Updated draft DTO
 */
export async function recordPick(draftId, pick) {
  const guestToken = getDraftGuestToken(draftId);
  const path = guestToken ? `/api/draft/guest/${draftId}/pick` : `/api/draft/${draftId}/pick`;
  const { data } = await api.post(path, pick, guestTokenRequestConfig(draftId));
  return data.data;
}

/**
 * Undo the most recent pick. Draft owner only.
 * @param {string} draftId
 * @returns {Promise<Object>} Updated draft DTO
 */
export async function undoPick(draftId) {
  const { data } = await api.post(`/api/draft/${draftId}/undo`);
  return data.data;
}

/**
 * Delete a draft and all associated picks. Draft owner only.
 * @param {string} draftId
 * @returns {Promise<void>}
 */
export async function deleteDraft(draftId) {
  await api.delete(`/api/draft/${draftId}`);
}

/**
 * Idempotent start trigger for a mock draft's AI-picks background batch (advances every
 * AI-controlled slot until the owner's next turn). No longer a synchronous full-loop call --
 * returns immediately with the current DraftDTO (mockPicksBatchStatus: "processing" if a batch
 * was just started or was already running). Callers should poll getDraftById to see picks stream
 * in and detect completion; default client timeout is fine now that this never waits on Claude.
 * @param {string} draftId
 * @returns {Promise<Object>} DraftDTO
 */
export async function simulateMockPicks(draftId) {
  const { data } = await api.post(`/api/draft/${draftId}/mock-picks`);
  return data.data;
}

/**
 * Check whether the user still has a mock draft available this week. Pro only.
 * @returns {Promise<{available: boolean}>}
 */
export async function getMockDraftUsage() {
  const { data } = await api.get('/api/draft/mock-draft-usage');
  return data.data;
}

// ─── AI Endpoints ─────────────────────────────────────────────────────────────

/**
 * Get the caller's daily AI usage summary. Pro only.
 * @returns {Promise<{used: number, limit: number, remaining: number, tier: string}>}
 */
export async function getAiUsage() {
  const { data } = await api.get('/api/ai/usage');
  return data.data;
}

/**
 * Request AI pick suggestions for the user's current draft turn (D1).
 * Free users get 1 use per draft; Pro users get 1 use per round.
 * @param {string} draftId
 * @returns {Promise<Object>} PickSuggestionResponse
 */
export async function suggestDraftPick(draftId) {
  // Claude-backed call can legitimately take >10s; 28s stays under API Gateway's 29s ceiling.
  const { data } = await api.post(`/api/ai/draft/${draftId}/suggest`, null, { timeout: 28000 });
  return data.data;
}

/**
 * Get AI predictions for each remaining pick in the current round (D3).
 * Free users get 1 use per draft; Pro users get up to 10 per draft.
 * @param {string} draftId
 * @returns {Promise<Object>} RoundPredictionResponse
 */
export async function getDraftRoundPrediction(draftId) {
  // Claude-backed call can legitimately take >10s; 28s stays under API Gateway's 29s ceiling.
  const { data } = await api.get(`/api/ai/draft/${draftId}/round-prediction`, { timeout: 28000 });
  return data.data;
}

/**
 * Get AI start/sit recommendations for the current week's lineup (S1). Pro only.
 * @param {string} leagueId
 * @param {number} week - current NFL week (1–18)
 * @returns {Promise<Object>} StartSitResponse
 */
export async function getStartSitRecommendation(leagueId, week) {
  // Claude-backed call can legitimately take >10s; 28s stays under API Gateway's 29s ceiling.
  const { data } = await api.post(`/api/ai/league/${leagueId}/start-sit`, null, {
    params: { week },
    timeout: 28000,
  });
  return data.data;
}

/**
 * Get an AI matchup-difficulty preview for a specific player (S2). Pro only.
 * Available every in-season week (rolling window, not gated to a particular week).
 * @param {string} playerName
 * @param {number} startWeek - first week of the window (typically the current NFL week)
 * @param {number} endWeek   - last week of the window, inclusive (typically startWeek+3, clamped to 18)
 * @param {string} [leagueId] - optional; when given, injects that league's recent-form/news context
 * @returns {Promise<Object>} MatchupPreviewResponse
 */
export async function getMatchupPreview(playerName, startWeek, endWeek, leagueId) {
  // Claude-backed call can legitimately take >10s; 28s stays under API Gateway's 29s ceiling.
  const params = { startWeek, endWeek };
  if (leagueId) params.leagueId = leagueId;
  const { data } = await api.get(`/api/ai/players/${encodeURIComponent(playerName)}/matchup-preview`, {
    params,
    timeout: 28000,
  });
  return data.data;
}

/**
 * Analyse a proposed trade and return ratings for both sides (S3). Pro only.
 * @param {string} leagueId
 * @param {string[]} givingPlayers - player names the user is trading away
 * @param {string[]} receivingPlayers - player names the user is receiving
 * @param {Object} [opts]
 * @param {number} [opts.opponentSlotIndex] - a league_teams slot the user is trading with (Mode B)
 * @param {string[]} [opts.theirRosterOverride] - the other side's roster; standalone = ad-hoc (Mode C)
 * @returns {Promise<Object>} TradeAnalysisResponse
 */
export async function analyzeTrade(leagueId, givingPlayers, receivingPlayers, opts = {}) {
  const body = { givingPlayers, receivingPlayers };
  if (opts.opponentSlotIndex !== undefined && opts.opponentSlotIndex !== null) {
    body.opponentSlotIndex = opts.opponentSlotIndex;
  }
  if (opts.theirRosterOverride && opts.theirRosterOverride.length > 0) {
    body.theirRosterOverride = opts.theirRosterOverride;
  }
  // Claude-backed call can legitimately take >10s; 28s stays under API Gateway's 29s ceiling.
  const { data } = await api.post(`/api/ai/league/${leagueId}/trade-analysis`, body, { timeout: 28000 });
  return data.data;
}

/**
 * Read a league team slot's stored roster (opponent-aware trade analysis, Part 3).
 * @param {string} leagueId
 * @param {number} slotIndex
 * @returns {Promise<Array<{playerName:string, position:string, nflTeam:string}>>}
 */
export async function getLeagueTeamRoster(leagueId, slotIndex) {
  const { data } = await api.get(`/api/league/${leagueId}/teams/${slotIndex}/roster`);
  return data.data;
}

/**
 * Replace a league team slot's roster (source="manual"). Owner slot -> 400.
 * @param {string} leagueId
 * @param {number} slotIndex
 * @param {Array<{playerName:string, position?:string, nflTeam?:string}>} roster
 */
export async function putLeagueTeamRoster(leagueId, slotIndex, roster) {
  const { data } = await api.put(`/api/league/${leagueId}/teams/${slotIndex}/roster`, roster);
  return data.data;
}

/**
 * Re-pull every non-owner team's roster for an imported league. Manual league -> 400.
 * @param {string} leagueId
 * @returns {Promise<{updated:number}>}
 */
export async function refreshOpponentRosters(leagueId) {
  const { data } = await api.post(`/api/league/${leagueId}/refresh-rosters`);
  return data.data;
}

/**
 * Seed every non-owner slot's roster from the league's source draft picks (follow-along
 * draft -> league). Hand-edited ("manual") slots are preserved. No source draft -> 400.
 * @param {string} leagueId
 * @returns {Promise<{updated:number}>}
 */
export async function populateRostersFromDraft(leagueId) {
  const { data } = await api.post(`/api/league/${leagueId}/populate-rosters-from-draft`);
  return data.data;
}

/**
 * Get an AI narrative profile of an opponent's drafting tendencies (O1). Pro only.
 * Cached server-side by opponent + draft-history hash, so a re-view of an unchanged profile
 * doesn't cost a weekly AI credit.
 * @param {string} opponentId
 * @returns {Promise<Object>} OpponentDraftProfileResponse
 */
export async function getOpponentDraftProfile(opponentId) {
  // Claude-backed call can legitimately take >10s; 28s stays under API Gateway's 29s ceiling.
  const { data } = await api.get(`/api/ai/opponent/${opponentId}/draft-profile`, { timeout: 28000 });
  return data.data;
}

/**
 * Force a fresh O1 opponent draft profile (the "Regenerate Scouting Report" button). Pro only.
 * Unlike getOpponentDraftProfile, this always calls Claude and always spends a weekly AI credit —
 * the server-side cache is skipped, so it works even when the opponent's linked-draft-ID list is
 * unchanged (e.g. new picks in an already-linked draft, or just wanting a fresh take).
 * @param {string} opponentId
 * @returns {Promise<Object>} OpponentDraftProfileResponse
 */
export async function regenerateOpponentDraftProfile(opponentId) {
  const { data } = await api.post(`/api/ai/opponent/${opponentId}/draft-profile`, null, { timeout: 28000 });
  return data.data;
}

/**
 * Check the status of a draft's AI grades (G1). Pro only. Async: the actual Claude call runs in
 * the background (see backend DraftInsightsService), so this is a pure, side-effect-free status
 * check -- safe to call on mount (to restore a mid-generation button state across a page refresh)
 * and on every poll tick. Never charges an AI credit or triggers generation itself; use
 * startDraftGrades for that.
 * @param {string} draftId
 * @returns {Promise<Object>} DraftGradesResponse with status "not_started"|"processing"|"complete"|"failed"
 */
export async function getDraftGradesStatus(draftId) {
  const { data } = await api.get(`/api/ai/draft/${draftId}/grades`);
  return data.data;
}

/**
 * Start generating AI letter grades for every team in a completed, non-mock draft (G1), or report
 * the status of a generation already in flight. Pro only. Idempotent: calling this while a fresh
 * generation is already running just returns its "processing" status again, without starting a
 * duplicate (paid) Claude call -- safe to call from a double-click or two open tabs. Returns
 * "complete" immediately (no charge) if this draft's grades were already generated.
 * @param {string} draftId
 * @returns {Promise<Object>} DraftGradesResponse
 */
export async function startDraftGrades(draftId) {
  const { data } = await api.post(`/api/ai/draft/${draftId}/grades`);
  return data.data;
}

// ─── Opponent Endpoints ────────────────────────────────────────────────────────

/**
 * List all opponents for the current user.
 * @returns {Promise<Array>} OpponentSummaryDTO list
 */
export async function getOpponents() {
  const { data } = await api.get('/api/opponents');
  return data.data || [];
}

/**
 * Create a new opponent.
 * @param {Object} payload - { displayName, nickname?, notes?, yearsInLeague?, championships? }
 * @returns {Promise<Object>} OpponentDTO
 */
export async function createOpponent(payload) {
  const { data } = await api.post('/api/opponents', payload);
  return data.data;
}

/**
 * Update an opponent's details.
 * @param {string} id - opponent UUID
 * @param {Object} payload - fields to update (all optional)
 * @returns {Promise<Object>} OpponentDTO
 */
export async function updateOpponent(id, payload) {
  const { data } = await api.put(`/api/opponents/${id}`, payload);
  return data.data;
}

/**
 * Delete an opponent.
 * @param {string} id - opponent UUID
 * @returns {Promise<void>}
 */
export async function deleteOpponent(id) {
  await api.delete(`/api/opponents/${id}`);
}

/**
 * Get full history for an opponent (linked drafts + picks, linked leagues).
 * @param {string} id - opponent UUID
 * @returns {Promise<Object>} OpponentHistoryDTO
 */
export async function getOpponentHistory(id) {
  const { data } = await api.get(`/api/opponents/${id}/history`);
  return data.data;
}

/**
 * Get all team slots for a draft, ordered by slotIndex.
 * @param {string} draftId
 * @returns {Promise<Array>} List<DraftTeamDTO>
 */
export async function getDraftTeams(draftId) {
  const { data } = await api.get(`/api/draft/${draftId}/teams`);
  return data.data || [];
}

/**
 * Link an opponent to a specific draft team slot.
 * @param {string} draftId
 * @param {number} slotIndex
 * @param {string} opponentId - opponent UUID
 * @returns {Promise<Object>} Updated DraftTeamDTO
 */
export async function linkOpponentToDraftSlot(draftId, slotIndex, opponentId) {
  const { data } = await api.put(`/api/draft/${draftId}/teams/${slotIndex}/opponent`, { opponentId });
  return data.data;
}

/**
 * Remove the opponent link from a draft team slot.
 * @param {string} draftId
 * @param {number} slotIndex
 * @returns {Promise<Object>} Updated DraftTeamDTO
 */
export async function unlinkOpponentFromDraftSlot(draftId, slotIndex) {
  const { data } = await api.delete(`/api/draft/${draftId}/teams/${slotIndex}/opponent`);
  return data.data;
}

/**
 * Get all team slots for a league, ordered by slotIndex.
 * @param {string} leagueId
 * @returns {Promise<Array>} List<LeagueTeamDTO>
 */
export async function getLeagueTeams(leagueId) {
  const { data } = await api.get(`/api/league/${leagueId}/teams`);
  return data.data || [];
}

/**
 * Link an opponent to a specific league team slot.
 * @param {string} leagueId
 * @param {number} slotIndex
 * @param {string} opponentId - opponent UUID
 * @returns {Promise<Object>} Updated LeagueTeamDTO
 */
export async function linkOpponentToLeagueSlot(leagueId, slotIndex, opponentId) {
  const { data } = await api.put(`/api/league/${leagueId}/teams/${slotIndex}/opponent`, { opponentId });
  return data.data;
}

/**
 * Remove the opponent link from a league team slot.
 * @param {string} leagueId
 * @param {number} slotIndex
 * @returns {Promise<Object>} Updated LeagueTeamDTO
 */
export async function unlinkOpponentFromLeagueSlot(leagueId, slotIndex) {
  const { data } = await api.delete(`/api/league/${leagueId}/teams/${slotIndex}/opponent`);
  return data.data;
}

/**
 * Link an opponent to a league team slot by externalTeamKey instead of slotIndex -- use this
 * (not linkOpponentToLeagueSlot) for post-import Manager Assignments, since league_teams and
 * draft_teams slotIndex numbering diverges for Yahoo/ESPN imports.
 * @param {string} leagueId
 * @param {string} externalTeamKey
 * @param {string} opponentId - opponent UUID
 * @returns {Promise<Object>} Updated LeagueTeamDTO
 */
export async function linkOpponentToLeagueSlotByExternalKey(leagueId, externalTeamKey, opponentId) {
  const { data } = await api.put(`/api/league/${leagueId}/teams/by-key/${externalTeamKey}/opponent`, { opponentId });
  return data.data;
}

/**
 * Remove the opponent link from a league team slot by externalTeamKey (see linkOpponentToLeagueSlotByExternalKey).
 * @param {string} leagueId
 * @param {string} externalTeamKey
 * @returns {Promise<Object>} Updated LeagueTeamDTO
 */
export async function unlinkOpponentFromLeagueSlotByExternalKey(leagueId, externalTeamKey) {
  const { data } = await api.delete(`/api/league/${leagueId}/teams/by-key/${externalTeamKey}/opponent`);
  return data.data;
}

export default api;
